import React, { forwardRef } from 'react';
import {
  FloatingPortal,
  FloatingFocusManager,
  FloatingOverlay,
  useMergeRefs,
  FloatingArrow,
} from '@floating-ui/react';
import { usePopoverNextContext } from '../../hooks/usePopoverNextContext';
import type { PopoverNextContentProps } from '../../PopoverNext.types';
import { st, classes } from './Content.st.css';
import { APPEND_TO } from '../../PopoverNext.constants';

const Content = forwardRef<HTMLDivElement, PopoverNextContentProps>(
  function Content({ style, ...props }, propRef) {
    const {
      dataHook,
      placement,
      focusManagerEnabled,
      context,
      appendTo,
      portalRoot,
      zIndex,
      popoverStyles,
      interactions,
      open,
      skin,
      transitionStyles,
      overlay,
      style: styleFromUser,
      showArrow,
      contentClassName,
      arrowProps,
    } = usePopoverNextContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);
    const { className, ...floatingProps } =
      interactions.getFloatingProps(props);

    if (!open) {
      return null;
    }

    const mergedStyles = {
      ...popoverStyles,
      zIndex,
      ...transitionStyles,
      ...styleFromUser,
      ...style,
    };

    const content = (
      <FloatingFocusManager context={context} disabled={!focusManagerEnabled}>
        <>
          {overlay === true ? <FloatingOverlay /> : (overlay ?? null)}
          <div
            ref={ref}
            data-hook={`${dataHook}-content`}
            data-zindex={zIndex}
            className={st(
              classes.root,
              { skin, placement, showArrow },
              className,
            )}
            style={mergedStyles}
            {...floatingProps}
          >
            {showArrow && (
              <FloatingArrow
                {...arrowProps}
                className={st(classes.arrow, { skin })}
              />
            )}
            <div className={st(classes.contentWrapper, {}, contentClassName)}>
              {props.children}
            </div>
          </div>
        </>
      </FloatingFocusManager>
    );

    if (appendTo === APPEND_TO.window || appendTo === APPEND_TO.viewport) {
      return <FloatingPortal>{content}</FloatingPortal>;
    }

    if (portalRoot) {
      return (
        <FloatingPortal root={portalRoot} preserveTabOrder={false}>
          {content}
        </FloatingPortal>
      );
    }

    return content;
  },
);

export default Content;
